/**
 * Host adapter: single contract between host and plugin.
 * RevealAdapter (presentation) and RemoteAdapter (remote) implement it.
 * @see docs/architecture/GREENFIELD-LECTURA-ARCHITECTURE.md
 */

import type { LecturaHostAdapter } from './core/types';

/**
 * Create adapter for Reveal.js presentation host.
 */
export function createRevealAdapter(): LecturaHostAdapter {
  let slideUnsubscribe = (): void => {};
  return {
    getPresId() {
      if (typeof window !== 'undefined') {
        const fromGlobal = String(window.__LECTURA_PRES_ID__ ?? '').trim();
        if (fromGlobal) return fromGlobal;
      }
      const revealWithId = document.querySelector('.reveal[data-lectura-presentation-id]');
      const fromAttr = revealWithId?.getAttribute('data-lectura-presentation-id');
      if (fromAttr && String(fromAttr).trim()) return String(fromAttr).trim();
      return 'deck';
    },
    getSlideIndex() {
      if (typeof Reveal === 'undefined') return 0;
      const idx = Reveal.getIndices?.().h;
      return typeof idx === 'number' && idx >= 0 ? idx : 0;
    },
    getTotalSlides() {
      if (typeof Reveal === 'undefined') return 0;
      const total = Reveal.getTotalSlides?.();
      if (typeof total === 'number' && total >= 0) return total;
      const slides = document.querySelectorAll('.reveal .slides > section');
      return slides.length;
    },
    getCurrentSlide() {
      if (typeof Reveal === 'undefined') return null;
      return Reveal.getCurrentSlide?.() ?? null;
    },
    getCurrentUser() {
      return (typeof window !== 'undefined' && window.__LECTURA_CURRENT_USER__) || null;
    },
    subscribeSlideChange(cb) {
      if (typeof Reveal !== 'undefined' && typeof Reveal.on === 'function') {
        const onChange = (): void => {
          cb();
        };
        Reveal.on('slidechanged', onChange);
        Reveal.on('fragmentshown', onChange);
        Reveal.on('fragmenthidden', onChange);
        slideUnsubscribe = () => {
          Reveal.off?.('slidechanged', onChange);
          Reveal.off?.('fragmentshown', onChange);
          Reveal.off?.('fragmenthidden', onChange);
        };
      }
      return slideUnsubscribe;
    },
    subscribeUserChange() {
      return () => {};
    },
    getContainer() {
      const wrap = document.querySelector('.rf-slides-chat-wrap') || document.body;
      return wrap as HTMLElement;
    },
    getDrawerMountPoint() {
      const wrap = document.querySelector('.rf-slides-chat-wrap') || document.body;
      return wrap as HTMLElement;
    },
    getHighlightRgb() {
      return (typeof getComputedStyle !== 'undefined' && document.documentElement)
        ? getComputedStyle(document.documentElement).getPropertyValue('--notes-tts-highlight-rgb').trim() || '100,100,200'
        : '100,100,200';
    },
    getBubbleBadgeElement() {
      return document.querySelector('.rf-fab-badge');
    },
    getSlideTitlePlainText() {
      if (typeof Reveal === 'undefined') return '';
      const slide = Reveal.getCurrentSlide?.() ?? null;
      if (!slide) return '';
      const heading = slide.querySelector('h1, h2, h3, h4, h5, h6');
      return (heading?.textContent || '').trim().slice(0, 300);
    }
  };
}

/**
 * Create adapter from window.__LECTURA_DRAWER_CONTEXT__ (remote host).
 */
export function createRemoteAdapter(): LecturaHostAdapter | null {
  const ctx = typeof window !== 'undefined' ? window.__LECTURA_DRAWER_CONTEXT__ : undefined;
  if (!ctx || typeof ctx.getPresId !== 'function' || typeof ctx.getSlideIndex !== 'function' || typeof ctx.getCurrentUser !== 'function') {
    return null;
  }
  return {
    getPresId: () => (typeof ctx.getPresId === 'function' ? ctx.getPresId() : 'deck'),
    getSlideIndex: () => {
      const n = typeof ctx.getSlideIndex === 'function' ? ctx.getSlideIndex() : 0;
      return typeof n === 'number' && n >= 0 ? n : 0;
    },
    getTotalSlides: () => {
      const n = typeof ctx.getTotalSlides === 'function' ? ctx.getTotalSlides() : 0;
      return typeof n === 'number' && n >= 0 ? n : 0;
    },
    getCurrentSlide: () => (typeof ctx.getCurrentSlide === 'function' ? ctx.getCurrentSlide() : null) ?? null,
    getCurrentUser: () => (typeof ctx.getCurrentUser === 'function' ? ctx.getCurrentUser() : null),
    subscribeSlideChange: (cb) => (typeof ctx.subscribeSlideChange === 'function' ? ctx.subscribeSlideChange(cb) : () => {}),
    subscribeUserChange: (cb) => (typeof ctx.subscribeUserChange === 'function' ? ctx.subscribeUserChange(cb) : () => {}),
    getContainer: () => (ctx.container ?? document.body),
    getDrawerMountPoint: () => (ctx.container ?? document.body),
    getHighlightRgb: () => (typeof ctx.getHighlightRgb === 'function' ? ctx.getHighlightRgb() : '100,100,200'),
    getBubbleBadgeElement: () => (typeof ctx.getBubbleBadge === 'function' ? ctx.getBubbleBadge() : null) ?? null,
    signInGoogle: typeof ctx.signInGoogle === 'function' ? ctx.signInGoogle.bind(ctx) : undefined,
    signInGithub: typeof ctx.signInGithub === 'function' ? ctx.signInGithub.bind(ctx) : undefined,
    signOut: typeof ctx.signOut === 'function' ? ctx.signOut.bind(ctx) : undefined,
    onDrawerOpen:
      typeof ctx.onOpen === 'function' || typeof ctx.onClose === 'function'
        ? (open: boolean) => {
            if (open && ctx.onOpen) ctx.onOpen();
            else if (!open && ctx.onClose) ctx.onClose();
          }
        : undefined
  };
}
